import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Loader2,
  Shield,
  CreditCard,
  ShoppingCart,
  Check,
  Home,
  ChevronLeft,
  BookOpen,
  MessageCircle,
} from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import { SERVICE_GRID } from "./StageServices";

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

export default function StageDecision({
  previewToggles,
  perServicePrice,
  homeCount,
  onSubscribe,
  onBack,
  actionLoading,
  planLabel,
  hostLogo,
}) {
  const enabled = useMemo(
    () => SERVICE_GRID.filter((s) => !!previewToggles?.[s.key]),
    [previewToggles]
  );
  const homes = Math.max(Number(homeCount) || 1, 1);
  const monthlyTotal = enabled.length * perServicePrice * homes;

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    enabled.forEach((s) => {
      const id = KEY_TO_DEMO_ID[s.key];
      if (id) set.add(id);
    });
    return set;
  }, [enabled]);

  return (
    <div className="relative w-full h-full grid grid-rows-[minmax(0,40%)_minmax(0,60%)] laptop:grid-rows-1 laptop:grid-cols-[1fr_1fr]">
      {/* Left — decision anchor */}
      <div className="relative px-6 laptop:px-14 py-4 laptop:py-6 order-2 laptop:order-1 min-h-0 flex flex-col justify-center overflow-y-auto laptop:overflow-visible">
        <div className="w-full max-w-[520px] mx-auto laptop:mx-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-2">
            Step 5 · Subscribe
          </p>
          <h2 className="text-[26px] laptop:text-[34px] font-extrabold text-[#111111] leading-[1.04]" style={{ letterSpacing: -0.6 }}>
            You're ready.
            <br />
            <span className="text-[#5F6368]">Go live with your concierge.</span>
          </h2>

          {/* Anchor card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-5 laptop:mt-7 rounded-[20px] bg-[#111111] text-white overflow-hidden"
          >
            <div className="p-5 laptop:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/70">
                    Your Plan
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981]">
                  <Check size={10} strokeWidth={3} />
                  Always included
                </span>
              </div>

              <div className="flex items-end gap-2 mb-4">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={monthlyTotal}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-[44px] laptop:text-[56px] font-extrabold leading-none tabular-nums"
                    style={{ letterSpacing: -1 }}
                  >
                    ${planLabel ? planLabel.replace("$", "") : monthlyTotal}
                  </motion.span>
                </AnimatePresence>
                <span className="text-[13px] font-semibold text-white/60 pb-2">/month</span>
              </div>

              <div className="flex items-center gap-3 text-[11px] text-white/70">
                <span className="flex items-center gap-1"><Home size={11} /> {homes} home{homes === 1 ? "" : "s"}</span>
                <span className="opacity-40">·</span>
                <span>{enabled.length} service{enabled.length === 1 ? "" : "s"}</span>
              </div>

              {/* Always included chips — platform features every plan gets */}
              <div className="mt-4 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <BookOpen size={10} strokeWidth={2} />
                  Branded Guidebook
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <MessageCircle size={10} strokeWidth={2} />
                  AI Guest Concierge
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <ShoppingCart size={10} strokeWidth={2} />
                  Pre-Arrival Groceries
                </span>
              </div>
            </div>

            {/* Selected services reel */}
            {enabled.length > 0 && (
              <div className="px-5 laptop:px-6 pb-5 laptop:pb-6 -mt-1">
                <div className="flex flex-wrap gap-1.5">
                  {enabled.slice(0, 8).map((s) => {
                    const Icon = s.icon;
                    return (
                      <span
                        key={s.key}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[11px] font-semibold text-white"
                      >
                        <Icon size={11} strokeWidth={2} />
                        {s.name}
                      </span>
                    );
                  })}
                  {enabled.length > 8 && (
                    <span className="inline-flex items-center px-2.5 py-1.5 rounded-full bg-white/10 text-[11px] font-semibold text-white/80">
                      +{enabled.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* CTA bar */}
            <div className="bg-white/5 px-5 laptop:px-6 py-4 flex items-center justify-between gap-3 flex-wrap border-t border-white/10">
              <div className="flex items-center gap-3 text-[11px] text-white/70">
                <span className="flex items-center gap-1"><Shield size={11} /> Cancel anytime</span>
                <span className="flex items-center gap-1"><CreditCard size={11} /> Secure checkout</span>
              </div>
              <button
                onClick={() => onSubscribe("basic", "stage_decision")}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[12px] bg-[#EF4949] text-white text-[14px] font-bold hover:bg-[#DC2626] transition-colors shadow-[0_8px_32px_rgba(239,73,73,0.2)] disabled:opacity-60"
              >
                {actionLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    Subscribe & go live
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </div>
          </motion.div>

          <button
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#888888] hover:text-[#111111] transition-colors"
          >
            <ChevronLeft size={13} />
            Rethink my selections
          </button>
        </div>
      </div>

      {/* Right — phone anchor */}
      <div className="relative order-1 laptop:order-2 min-h-0 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 50% 45%, rgba(239,73,73,0.1), transparent 60%)",
          }}
        />
        <div className="relative h-full flex items-center justify-center py-2 laptop:py-6">
          <PhoneAnchor stage={5} enabledServices={enabledServices} hostLogo={hostLogo} />
        </div>
      </div>
    </div>
  );
}
